import React, { useState, useEffect } from 'react';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Card } from '../components/common/Card';
import { Select } from '../components/common/Select';
import api from '../services/api';
import { useNotification } from '../contexts/NotificationContext';

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

interface Leave {
  id: number;
  leaveType: string;
  startDate: string;
  endDate: string;
  daysInMonth: number;
  reason?: string;
}

interface UserLeave {
  userId: number;
  fullName: string;
  email: string;
  leaves: Leave[];
  totalDays: number;
}

interface ReportData {
  month: number;
  year: number;
  monthName: string;
  users: UserLeave[];
  summary: {
    totalUsers: number;
    totalLeaveDays: number;
  };
}

const MonthlyLeaveReport: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [excludedUserIds, setExcludedUserIds] = useState<number[]>([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  
  const { showNotification } = useNotification();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.users || response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      showNotification({ 
        type: 'error', 
        title: 'Błąd ładowania użytkowników' 
      });
    }
  };

  const generateReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        year: year.toString(),
        month: month.toString(),
      });
      
      excludedUserIds.forEach(id => {
        params.append('excluded_user_ids', id.toString());
      });

      const response = await api.get(`/reports/monthly-leave?${params.toString()}`);
      setReportData(response.data.data);
      
      showNotification({ 
        type: 'success', 
        title: 'Raport został wygenerowany' 
      });
    } catch (error: any) {
      console.error('Error generating report:', error);
      showNotification({ 
        type: 'error', 
        title: error.response?.data?.message || 'Błąd generowania raportu' 
      });
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        year: year.toString(),
        month: month.toString(),
      });
      
      excludedUserIds.forEach(id => {
        params.append('excluded_user_ids', id.toString());
      });

      const response = await api.get(`/reports/monthly-leave/export?${params.toString()}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `raport_urlopow_${year}_${month.toString().padStart(2, '0')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showNotification({ 
        type: 'success', 
        title: 'Raport został wyeksportowany do Excel' 
      });
    } catch (error: any) {
      console.error('Error exporting report:', error);
      showNotification({ 
        type: 'error', 
        title: error.response?.data?.message || 'Błąd eksportu do Excel' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUserExclusionChange = (userId: number, isExcluded: boolean) => {
    if (isExcluded) {
      setExcludedUserIds(prev => [...prev, userId]);
    } else {
      setExcludedUserIds(prev => prev.filter(id => id !== userId));
    }
  };

  const months = [
    { value: 1, label: 'Styczeń' },
    { value: 2, label: 'Luty' },
    { value: 3, label: 'Marzec' },
    { value: 4, label: 'Kwiecień' },
    { value: 5, label: 'Maj' },
    { value: 6, label: 'Czerwiec' },
    { value: 7, label: 'Lipiec' },
    { value: 8, label: 'Sierpień' },
    { value: 9, label: 'Wrzesień' },
    { value: 10, label: 'Październik' },
    { value: 11, label: 'Listopad' },
    { value: 12, label: 'Grudzień' }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">
          Raport miesięczny urlopów dla księgowej
        </h1>
      </div>

      <Card>
        <h3 className="text-lg font-semibold mb-4">Parametry raportu</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Select
            label="Rok"
            value={year.toString()}
            onChange={(e) => setYear(parseInt(e.target.value))}
          >
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </Select>

          <Select
            label="Miesiąc"
            value={month.toString()}
            onChange={(e) => setMonth(parseInt(e.target.value))}
          >
            {months.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </Select>
        </div>

        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3">Wyklucz użytkowników z raportu:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto border rounded-lg p-4">
            {users.map(user => (
              <label key={user.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                <input
                  type="checkbox"
                  checked={excludedUserIds.includes(user.id)}
                  onChange={(e) => handleUserExclusionChange(user.id, e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">
                  {user.first_name} {user.last_name}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-4">
          <Button 
            onClick={generateReport} 
            disabled={loading}
            variant="primary"
          >
            {loading ? 'Generuję...' : 'Generuj raport'}
          </Button>
          
          <Button 
            onClick={exportToExcel} 
            disabled={loading || !reportData}
            variant="outline"
          >
            {loading ? 'Eksportuję...' : 'Eksportuj do Excel'}
          </Button>
        </div>
      </Card>

      {reportData && (
        <Card>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              Raport urlopów - {reportData.monthName} {reportData.year}
            </h3>
            <div className="text-sm text-gray-600">
              Użytkownicy: {reportData.summary.totalUsers} | 
              Łącznie dni urlopowych: {reportData.summary.totalLeaveDays}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lp.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Imię i nazwisko
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Typ urlopu
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data od
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data do
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Liczba dni
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uwagi
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.users.map((user, userIndex) => (
                  <React.Fragment key={user.userId}>
                    {user.leaves.map((leave, leaveIndex) => (
                      <tr key={`${user.userId}-${leave.id}`} className={userIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {userIndex + 1}.{leaveIndex + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.fullName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {leave.leaveType}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(leave.startDate).toLocaleDateString('pl-PL')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(leave.endDate).toLocaleDateString('pl-PL')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {leave.daysInMonth}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {leave.reason || '-'}
                        </td>
                      </tr>
                    ))}
                    {user.leaves.length > 1 && (
                      <tr className="bg-yellow-50 border-t-2 border-yellow-200">
                        <td className="px-6 py-2 text-sm font-bold text-gray-900" colSpan={2}>
                          RAZEM {user.fullName}:
                        </td>
                        <td className="px-6 py-2 text-sm text-gray-900" colSpan={3}></td>
                        <td className="px-6 py-2 text-sm font-bold text-gray-900">
                          {user.totalDays}
                        </td>
                        <td className="px-6 py-2 text-sm text-gray-900"></td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                
                {reportData.users.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                      Brak urlopów w wybranym miesiącu
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default MonthlyLeaveReport;